import PropTypes from 'prop-types';
import React from 'react';
import styled, { useTheme } from 'styled-components';
import { Input } from 'components/FormFields';
import { ActionIcon, Icon, orange, PlainButton, tokens } from 'components/v3';
import { LIMITS } from '../../shared/fieldTypes';

// Add / rename / remove a list of short texts: options, table and repeater
// columns, matrix rows and columns (design: OptionsEditor). No reordering
// in v1 (plan §8 D12). `min` / `max` keep the list inside the server's
// limits: no remove button at the minimum, Add disabled at the maximum.

const AddButton = styled(PlainButton)`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 0;
  font-size: 13px;
  font-weight: 600;
  color: ${orange(1)};

  &:disabled {
    opacity: 0.45;
  }
`;

const capital = (word) => word.charAt(0).toUpperCase() + word.slice(1);

export const nextItemLabel = (items, noun) => {
  const base = capital(noun);
  const taken = new Set(items.map((i) => String(i).trim().toLowerCase()));
  let n = items.length + 1;
  while (taken.has(`${base} ${n}`.toLowerCase())) n += 1;
  return `${base} ${n}`;
};

const ListEditor = ({ label, items, noun, min, max, onChange }) => {
  const t = tokens(useTheme());
  const list = Array.isArray(items) ? items : [];
  const cap = capital(noun);
  const id = `fb-list-${label.replace(/\W+/g, '-').toLowerCase()}`;
  const set = (index, value) =>
    onChange(list.map((item, i) => (i === index ? value : item)));
  const remove = (index) => onChange(list.filter((_, i) => i !== index));
  return (
    <div style={{ marginBottom: 18 }}>
      <div
        id={id}
        style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '1px',
          textTransform: 'uppercase',
          color: t.faint,
          marginBottom: 10,
        }}
      >
        {label}
      </div>
      <div
        role="group"
        aria-labelledby={id}
        style={{ display: 'grid', gap: 8, marginBottom: 6 }}
      >
        {list.map((item, i) => (
          // Rows are positional: an option's text is what the admin edits.
          <div
            key={i}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <Input
                aria-label={`${cap} ${i + 1}`}
                value={String(item)}
                maxLength={LIMITS.optionText}
                onChange={(e) => set(i, e.target.value)}
              />
            </div>
            {list.length > min ? (
              <ActionIcon
                icon="x"
                title={`Remove ${noun} ${i + 1}`}
                danger
                onClick={() => remove(i)}
              />
            ) : (
              <span aria-hidden="true" style={{ width: 38, flexShrink: 0 }} />
            )}
          </div>
        ))}
      </div>
      <AddButton
        disabled={list.length >= max}
        onClick={() => onChange([...list, nextItemLabel(list, noun)])}
      >
        <Icon name="plus" size={14} color="currentColor" />
        {`Add ${noun}`}
      </AddButton>
    </div>
  );
};

ListEditor.propTypes = {
  label: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(PropTypes.string),
  noun: PropTypes.string,
  min: PropTypes.number,
  max: PropTypes.number,
  onChange: PropTypes.func.isRequired,
};

ListEditor.defaultProps = {
  items: [],
  noun: 'option',
  min: 0,
  max: LIMITS.options,
};

export default ListEditor;
